import AgoraRTC, {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ILocalAudioTrack,
  ILocalVideoTrack
} from "agora-rtc-sdk-ng";

type StreamCallback = (stream: MediaStream) => void;
type VoidCallback = () => void;
type BooleanCallback = (value: boolean) => void;

class AgoraService {
  private client: IAgoraRTCClient | null = null;
  private localAudioTrack: ILocalAudioTrack | null = null;
  private localVideoTrack: ILocalVideoTrack | null = null;

  private onLocalStream: StreamCallback | null = null;
  private onRemoteStream: StreamCallback | null = null;
  private onUserLeftCb: VoidCallback | null = null;
  private onRemoteAudioMuted: BooleanCallback | null = null;

  on(
    event: "localStream" | "remoteStream" | "userLeft" | "remoteAudioMuted",
    cb: StreamCallback | VoidCallback | BooleanCallback
  ) {
    if (event === "localStream") this.onLocalStream = cb as StreamCallback;
    if (event === "remoteStream") this.onRemoteStream = cb as StreamCallback;
    if (event === "userLeft") this.onUserLeftCb = cb as VoidCallback;
    if (event === "remoteAudioMuted")
      this.onRemoteAudioMuted = cb as BooleanCallback;
  }

  async joinChannel(
    appId: string,
    channelName: string,
    token: string,
    uid: number
  ) {
    this.client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });

    this.client.on("user-published", async (user, mediaType) => {
      if (mediaType === "audio") {
        this.onRemoteAudioMuted?.(false);
      }

      try {
        await this.client!.subscribe(user, mediaType);
      } catch (subscribeError) {
        console.warn(
          "[AgoraService] Falha ao subscrever ao stream remoto:",
          subscribeError
        );
        return;
      }

      if (mediaType === "audio") {
        user.audioTrack?.play();
      }

      this.emitRemoteStream(user);
    });

    this.client.on("user-unpublished", (user, mediaType) => {
      if (mediaType === "audio") {
        this.onRemoteAudioMuted?.(true);
      }

      this.emitRemoteStream(user);
    });

    // Cobre tanto setMuted (user-info-updated) quanto setEnabled
    // (user-published/unpublished) no cliente remoto; ambos são idempotentes.
    this.client.on("user-info-updated", (uid, msg) => {
      if (msg === "mute-audio") this.onRemoteAudioMuted?.(true);
      if (msg === "unmute-audio") this.onRemoteAudioMuted?.(false);
    });

    this.client.on("user-left", () => {
      this.onUserLeftCb?.();
    });

    // Criar tracks de áudio e vídeo com tratamento de erro
    try {
      this.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    } catch (audioError: any) {
      console.error("[AgoraService] Erro ao acessar microfone:", audioError);
      if (audioError.code === "NOT_READABLE") {
        throw new Error("Microfone em uso por outro aplicativo. Feche outros apps que usam o microfone e tente novamente.");
      }
      throw audioError;
    }

    try {
      this.localVideoTrack = await AgoraRTC.createCameraVideoTrack();
    } catch (videoError: any) {
      console.error("[AgoraService] Erro ao acessar câmera:", videoError);
      if (videoError.code === "NOT_READABLE") {
        // Limpar o áudio já criado
        this.localAudioTrack?.close();
        this.localAudioTrack = null;
        throw new Error("Câmera em uso por outro aplicativo. Feche outros apps que usam a câmera (Teams, Zoom, etc) e tente novamente.");
      }
      throw videoError;
    }

    this.emitLocalStream();

    await this.client.join(appId, channelName, token, uid);
    await this.client.publish([this.localAudioTrack, this.localVideoTrack]);
  }

  private emitLocalStream() {
    if (!this.localVideoTrack || !this.localAudioTrack) return;

    const localStream = new MediaStream();
    localStream.addTrack(this.localVideoTrack.getMediaStreamTrack());
    localStream.addTrack(this.localAudioTrack.getMediaStreamTrack());
    this.onLocalStream?.(localStream);
  }

  private emitRemoteStream(user: IAgoraRTCRemoteUser) {
    const remoteStream = new MediaStream();

    if (user.videoTrack) {
      remoteStream.addTrack(user.videoTrack.getMediaStreamTrack());
    }
    if (user.audioTrack) {
      remoteStream.addTrack(user.audioTrack.getMediaStreamTrack());
    }

    this.onRemoteStream?.(remoteStream);
  }

  setMuted(muted: boolean) {
    this.localAudioTrack?.setEnabled(!muted);
  }

  async setCameraOff(off: boolean) {
    if (!this.localVideoTrack) return;

    await this.localVideoTrack.setEnabled(!off);

    if (!off) {
      this.emitLocalStream();
    }
  }

  async leave() {
    this.localAudioTrack?.close();
    this.localVideoTrack?.close();
    this.localAudioTrack = null;
    this.localVideoTrack = null;

    if (this.client) {
      await this.client.leave();
      this.client = null;
    }

    this.onLocalStream = null;
    this.onRemoteStream = null;
    this.onRemoteAudioMuted = null;
  }
}

export const agoraService = new AgoraService();
